import { PopoverForOverflownText, TableColumn, TableRow } from '@lifebit-ai/lds'
import React from 'react'

import * as ExportRequestTableStyles from 'modules/requests/ExportAndAccessRequestsTable/RequestsTable.styles'
import { AssigneeColumnContent } from 'modules/requests/components/AssigneeColumnContent'
import { LabelsColumnContent } from 'modules/requests/components/LabelsColumnContent'
import { DataAccessRequestStatus } from 'modules/requests/data-access-requests/components/DataAccessRequestStatus'
import type { DataAccessRequestVerificationListItem } from 'services/data-access-requests/types'
import { formatDate } from 'utils/date'

const dataAccessRequestsTableColumns: TableColumn<DataAccessRequestVerificationListItem>[] = [
  {
    title: 'Status',
    id: 'status',
    render: (row: TableRow<DataAccessRequestVerificationListItem>) => {
      return (
        <DataAccessRequestStatus reviewSubStatus={row.original.reviewSubStatus} status={row.original.status} />
      )
    },
    width: 150
  },
  {
    // Display-only: the verification list endpoint takes no sort/order param, so no column here is sortable yet.
    title: 'Study ID',
    id: 'studyId',
    render: (row: TableRow<DataAccessRequestVerificationListItem>) => {
      const studyId = row.original.studyId ?? '-'

      return <PopoverForOverflownText title={studyId}>{studyId}</PopoverForOverflownText>
    },
    width: 140
  },
  {
    title: 'Dataset',
    id: 'datasetName',
    width: 240,
    render: (row: TableRow<DataAccessRequestVerificationListItem>) => {
      const { datasetName } = row.original

      return (
        <ExportRequestTableStyles.DatasetFileCellContainer>
          <ExportRequestTableStyles.DatasetIcon aria-hidden />
          <ExportRequestTableStyles.DatasetFileCellText>
            <PopoverForOverflownText title={datasetName}>{datasetName}</PopoverForOverflownText>
          </ExportRequestTableStyles.DatasetFileCellText>
        </ExportRequestTableStyles.DatasetFileCellContainer>
      )
    }
  },
  {
    title: 'Labels',
    id: 'labelIds',
    width: 180,
    render: (row: TableRow<DataAccessRequestVerificationListItem>) => {
      return <LabelsColumnContent labels={row.original.labels} />
    }
  },
  {
    title: 'Requester workspace',
    id: 'requesterWorkspaceName',
    render: (row: TableRow<DataAccessRequestVerificationListItem>) => {
      const requesterWorkspaceName = row.original.requesterWorkspaceName ?? '-'

      return <PopoverForOverflownText title={requesterWorkspaceName}>{requesterWorkspaceName}</PopoverForOverflownText>
    },
    width: 180
  },
  {
    title: 'Requested by',
    id: 'requestedBy',
    width: 220,
    render: (row: TableRow<DataAccessRequestVerificationListItem>) => {
      const email = row.original.requestingUser?.email ?? '-'

      return <PopoverForOverflownText title={email}>{email}</PopoverForOverflownText>
    }
  },
  {
    title: 'Assignee',
    id: 'assignedTo',
    width: 180,
    render: (row: TableRow<DataAccessRequestVerificationListItem>) => (
      <AssigneeColumnContent assignedTo={row.original.assignedTo} />
    )
  },
  {
    title: 'Requested on',
    id: 'createdAt',
    render: (row: TableRow<DataAccessRequestVerificationListItem>) => {
      return formatDate(row.original.createdAt)
    },
    width: 200
  }
]

// Status and Study ID stay pinned through the table's fixed `pinning.current`, so no column is user-pinnable:
// leaving `canPin` unset would turn every header into a menu whose only action is a Pin that never applies.
export const getDataAccessRequestsTableColumns = (): TableColumn<DataAccessRequestVerificationListItem>[] =>
  dataAccessRequestsTableColumns.map(column => ({ ...column, canPin: false }))
